
/* eslint-disable class-methods-use-this */
import { Component, Vue } from 'vue-property-decorator';
import { Alert } from '@/core/types/alert';
import { AccountNumberValidation, ConfirmValidation, RequiredValidation } from '@kasasa/kloans-common-ui/dist/validations';
import { numeric, required, sameAs } from 'vuelidate/lib/validators';
import { ProfileLinkedAccountAccountType } from '@/profile/enums/profile-linked-account-account-type';
import { AlertTypeEnum } from '@/core/types/alert-type-enum';
import { CoreHelper } from '@/core/helper';
import { ProfileAddBankAccountRequest } from '@/profile/types/profile-add-bank-account-request';
import { AccountType } from '@/core/enums/account-type';
import { ProfileAPIService } from '@/profile/services/api';
import { AxiosError } from 'axios';
import { exactLength } from '@kasasa/kloans-common-ui/dist/validations/validators';
import * as ValidationMessages from '@kasasa/kloans-common-ui/dist/validations/messages';
import { UtilHelper } from '@/util/helper';

const ADD_ACCOUNT_SUCCESS_MESSAGE = 'Account Successfully Added.';
const accountNameValidator = (val: string) => /^[a-zA-Z ]+$/.test(val);

Component.registerHooks(['validations']);

@Component
export default class ProfileAddLinkedAccount extends Vue {
	public accountHolderName = '';

	public accountNickname = '';

	public accountNumber = '';

	public accountType = ProfileLinkedAccountAccountType.CHECKING;

	public accountTypeOptions = [
		{
			value: ProfileLinkedAccountAccountType.CHECKING,
			text: ProfileLinkedAccountAccountType.CHECKING.toUpperCase(),
		},
		{
			value: ProfileLinkedAccountAccountType.SAVINGS,
			text: ProfileLinkedAccountAccountType.SAVINGS.toUpperCase(),
		},
	];

	public alert: Alert | null = null;

	public confirmAccountNumber = '';

	public paymentRefUrl = '';

	public isAddAccountLoading = false;

	public isOwner = false;

	public numberOnly = UtilHelper.restrictKeyboardEventToNumber;

	public routingNumber = '';

	public wasAddAccountSuccessful = false;

	get accountNumberErrors(): string[] {
		return AccountNumberValidation.errors(this, 'accountNumber');
	}

	get confirmAccountNumberErrors(): string[] {
		return ConfirmValidation.errors(this, 'confirmAccountNumber', 'accountNumber');
	}

	get routingNumberErrors(): string[] {
		const errors: string[] = RequiredValidation.errors(this, 'routingNumber');

		if (!this.$v.routingNumber.$dirty) {
			return [];
		}

		if (!this.$v.routingNumber.exactLength) {
			errors.push(ValidationMessages.exactLength.replace('{1}', '9'));
		}

		return errors;
	}

	get accountNicknameErrors(): string[] {
		const errors: string[] = [];
		const input = this.$v.accountNickname;

		if (!input?.$dirty) {
			return errors;
		}

		if (input.accountNickname !== undefined && !input.accountNickname) {
			errors.push('Field can only contain characters A-Z');
		}

		return errors;
	}

	get accountHolderNameErrors(): string[] {
		const errors: string[] = [];
		const input = this.$v.accountHolderName;

		if (!input?.$dirty) {
			return errors;
		}

		if (input.accountName !== undefined && !input.accountName) {
			errors.push('Field can only contain characters A-Z');
		}

		return errors;
	}

	public async addAcount(): Promise<void> {
		const { type, fid } = this.$route.query;

		this.isAddAccountLoading = true;
		this.wasAddAccountSuccessful = false;

		try {
			let request: ProfileAddBankAccountRequest = {
				name: this.accountNickname,
				accountNumber: this.accountNumber,
				routingNumber: this.routingNumber,
				product: type as AccountType,
				accountType: this.accountType,
			};

			if (type === AccountType.LOAN) {
				request = {
					...request,
					...{
						fiId: Number(fid),
						holderName: this.accountHolderName,
					},
				};
			}

			await ProfileAPIService.addBankAccount(request);

			this.wasAddAccountSuccessful = true;

			this.alert = {
				type: AlertTypeEnum.SUCCESS,
				message: ADD_ACCOUNT_SUCCESS_MESSAGE,
			};

			if (this.paymentRefUrl) {
				window.location.href = this.paymentRefUrl;
			}
		} catch (error) {
			const err = error as AxiosError;
			const { errors } = err?.response?.data?.error;
			const message = (errors && errors.length > 0)
				? errors[0].message
				: CoreHelper.getHttpErrorMessage(error);

			this.alert = {
				type: AlertTypeEnum.ERROR,
				message,
			};
		}

		this.isAddAccountLoading = false;
	}

	public onRoutingNumberInput(input: string): void {
		this.$nextTick(() => {
			this.routingNumber = input.slice(0, 9);
			this.$v.routingNumber.$touch();
		});
	}

	mounted(): void {
		if (this.$route.params) {
			if (this.$route.params.paymentRefUrl) {
				this.paymentRefUrl = this.$route.params.paymentRefUrl;
			}
		}
	}

	public validations() {
		const { type } = this.$route.query;

		return {
			accountNumber: {
				required,
				numeric,
			},
			confirmAccountNumber: {
				required,
				numeric,
				sameAsAccountNumber: sameAs('accountNumber'),
			},
			routingNumber: {
				required,
				exactLength: exactLength(9),
			},
			accountNickname: {
				required,
				accountNickname: accountNameValidator,
			},
			accountHolderName: type === AccountType.LOAN
				? {
					required,
					accountName: accountNameValidator,
				}
				: {
					accountName: accountNameValidator,
				},
			isOwner: {
				sameAs: sameAs(() => true),
			},
		};
	}
}
